import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../app/providers/AuthProvider";
import { authRoutes } from "../config/authRoutes";
import {
  chooseAccount,
  getSelectableAccounts,
} from "../api/accountSelection";

const HAS_MULTIPLE_ACCOUNTS_KEY = "accountSelection.hasMultipleAccounts";

function getMultipleAccountsStorageKey(email) {
  return email ? `${HAS_MULTIPLE_ACCOUNTS_KEY}:${email.toLowerCase()}` : HAS_MULTIPLE_ACCOUNTS_KEY;
}

export function useAccountSelection() {
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [error, setError] = useState("");

  const hasAccounts = accounts.length > 0;
  const accountsLabel = useMemo(() => {
    const count = accounts.length || Number(user?.accountsCount || 0);
    return `${count} ${count === 1 ? "conta encontrada" : "contas encontradas"}`;
  }, [accounts.length, user?.accountsCount]);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const result = await getSelectableAccounts();
      setAccounts(result.accounts);
      localStorage.setItem(
        getMultipleAccountsStorageKey(user?.email),
        result.accounts.length > 1 ? "true" : "false"
      );
    } catch (loadError) {
      setError(loadError?.message || "Não foi possível carregar suas contas.");
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  async function selectAccount(accountId) {
    if (!accountId || refreshing) return;

    setSelectedAccountId(accountId);
    setRefreshing(true);
    setError("");

    try {
      await chooseAccount(accountId);
      const refreshedUser = await refreshUser(true);

      if (!refreshedUser?.accountId || refreshedUser?.accountSelectionRequired) {
        throw new Error("Não foi possível confirmar a conta selecionada. Tente novamente.");
      }

      navigate(authRoutes.dashboard, { replace: true, state: { user: refreshedUser } });
    } catch (selectError) {
      setError(selectError?.message || "Não foi possível acessar esta conta.");
      setSelectedAccountId("");
    } finally {
      setRefreshing(false);
    }
  }

  return {
    accounts,
    accountsLabel,
    error,
    hasAccounts,
    loading,
    refreshing,
    reload: loadAccounts,
    selectAccount,
    selectedAccountId,
  };
}
